import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock3, CreditCard, LayoutDashboard, Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { useViewPassStatus } from '../../api/viewPass';
import { currentRouteTarget, privateAccessPath } from '../../lib/marketplaceRoutes';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../ui/Button';

const formatRemaining = (seconds: number) => {
  const minutes = Math.max(0, Math.ceil(seconds / 60));
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

const LoadingGate: React.FC = () => (
  <div className="grid min-h-screen place-items-center bg-background px-4">
    <div className="flex items-center gap-3 text-sm text-[color:var(--color-text-secondary)]">
      <Clock3 className="h-5 w-5 animate-pulse text-primary-500" />
      Checking private intelligence access...
    </div>
  </div>
);

const Paywall: React.FC<{ error?: string; returnTo: string }> = ({ error, returnTo }) => (
  <div className="min-h-screen bg-background px-4 py-6 text-[color:var(--color-text-primary)] sm:px-6 lg:px-8">
    <div className="mx-auto grid max-w-5xl gap-4 lg:grid-cols-[1.1fr_0.9fr]">
      <section className="rounded-lg border border-glass-border bg-glass p-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg border border-primary-500/25 bg-primary-500/10 p-2">
            <Lock className="h-5 w-5 text-primary-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-primary-500">Pay-per-view private access</p>
            <h1 className="mt-1 text-xl font-semibold tracking-normal sm:text-2xl">Private Intelligence</h1>
          </div>
        </div>
        <p className="mt-3 max-w-3xl text-sm leading-5 text-[color:var(--color-text-secondary)]">
          Public intelligence stays open. A private pass unlocks timed depth for dossiers, full
          evidence, model conflicts, watchlist changes, source freshness, and private Ask context.
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {[
            [
              'Dossier depth',
              'Open deeper token, market, and signal evidence during the active window.',
            ],
            [
              'x402 exact pay',
              'Every pass is a separate wallet-confirmed payment. No silent renewal.',
            ],
            [
              'Read-only boundary',
              'No custody, execution, admin actions, bulk exports, or API scale.',
            ],
          ].map(([title, copy]) => (
            <div key={title} className="rounded-lg border border-glass-border bg-background p-3">
              <p className="text-sm font-semibold">{title}</p>
              <p className="mt-1.5 text-sm leading-5 text-[color:var(--color-text-secondary)]">
                {copy}
              </p>
            </div>
          ))}
        </div>
        {error && (
          <p className="mt-4 rounded-lg border border-red-400/30 bg-red-400/10 p-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={privateAccessPath(returnTo, 'unlock-workspace')}>
            <Button type="button" variant="primary" leftIcon={<CreditCard className="h-4 w-4" />}>
              Get Intelligence Pass
            </Button>
          </Link>
          <Link to="/market">
            <Button type="button" variant="secondary" leftIcon={<Sparkles className="h-4 w-4" />}>
              Public Main
            </Button>
          </Link>
        </div>
      </section>

      <aside className="space-y-3">
        <div className="rounded-lg border border-amber-400/25 bg-amber-400/10 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <LayoutDashboard className="h-4 w-4 text-amber-600" />
            What the pass opens
          </div>
          <ul className="mt-3 space-y-2 text-sm leading-6 text-[color:var(--color-text-secondary)]">
            <li>Private workspace and token dossier depth.</li>
            <li>Full evidence, source freshness, model conflicts, and watchlist changes.</li>
            <li>Timed access that expires by server time.</li>
          </ul>
        </div>
        <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/10 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Wallet trust boundary
          </div>
          <p className="mt-2 text-sm leading-6 text-[color:var(--color-text-secondary)]">
            Connecting a wallet is not payment authorization. Each access window requires a separate
            exact payment confirmation.
          </p>
        </div>
      </aside>
    </div>
  </div>
);

export const PrivateAccessGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const role = user?.role;
  const hasRoleAccess = role === 'admin' || role === 'approved';
  const statusQuery = useViewPassStatus(!hasRoleAccess);
  const returnTo = currentRouteTarget(location);

  if (hasRoleAccess) {
    return <>{children}</>;
  }

  const activeSession = statusQuery.data?.active_session;
  const hasAccess = statusQuery.data?.has_private_access === true;

  if (statusQuery.isLoading) {
    return <LoadingGate />;
  }

  if (statusQuery.isError) {
    return (
      <Paywall
        error={
          statusQuery.error instanceof Error
            ? statusQuery.error.message
            : 'Could not check private intelligence access.'
        }
        returnTo={returnTo}
      />
    );
  }

  if (!hasAccess) {
    return <Paywall returnTo={returnTo} />;
  }

  return (
    <>
      {activeSession && (
        <div className="sticky top-0 z-40 border-b border-amber-400/25 bg-amber-400/10 px-4 py-2 text-center text-sm font-medium text-amber-700">
          Private pass active: {formatRemaining(activeSession.remaining_seconds)} remaining.
        </div>
      )}
      {children}
    </>
  );
};

export default PrivateAccessGuard;
